/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { withRouter } from 'react-router-dom';

import PackageOrderList from './PackageOrderList';
import PackageNewOrderList from './PackageNewOrderList';
import PackagePackedOrderList from './PackagePackedOrderList';
import { SearchOrdersById } from '../apollo/queries';

const SEARCH_ORDERS_BY_ID = gql`${SearchOrdersById}`;

const WORKER_ORDER_STATUS = Object.freeze({
  NOT_STARTED: 0,
  PP_ASSIGNED: 1,
  PP_IN_PROGRESS: 2,
  PP_COMPLETED: 3,
  BILLING_ASSIGNED: 4,
  BILLING_IN_PROGRESS: 5,
  BILLING_COMPLETED: 6,
  RIDER_ASSIGN_MANUAL: 7,
  RIDER_ASSIGNED: 8,
  RIDER_OUT_FOR_DELIVERY: 9,
  RIDER_DELIVERED: 10,
  CANCELLED: 11,
});

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function PackingWorkerTab() {
  const [value, setValue] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const previousSearch = useRef('');
  const scrollRef = useRef(null);

  const [searchOrders, { data }] = useLazyQuery(SEARCH_ORDERS_BY_ID, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    localStorage.removeItem('PackingTab');
    localStorage.removeItem('Tab');
  }, []);

   // Tab change handler
  const handleChange = (event, newValue) => {
    if (value !== newValue) {
      setValue(newValue);
  // Clear previous search when switching tabs
      // setSearchText('');
      // setSearchResult([]);
      // previousSearch.current = '';
    }
  };
  // Manual search (click or Enter)
  const handleManualSearch = () => {
    const value = searchText.trim();
    if (!value) {
      setSearchResult([]);
      previousSearch.current = '';
      return;
    }
    if (value !== previousSearch.current) {
      previousSearch.current = value;
      searchOrders({ variables: { orderId: value, limit: 10, skip: 0 } });
    }
  };

  // Debounced auto search
  useEffect(() => {
    const value = searchText.trim();
    if (value === '' || value === previousSearch.current) return;

    const handler = setTimeout(() => {
      previousSearch.current = value;
      searchOrders({ variables: { orderId: value, limit: 10, skip: 0 } });
    }, 500);

    return () => clearTimeout(handler);
  }, [searchText, searchOrders]);

  // Handle search result and tab switch
  useEffect(() => {
    if (data && data.searchOrdersById && data.searchOrdersById.orders) {
      const allOrders = data.searchOrdersById.orders;
      setSearchResult(allOrders);

      // Scroll to the first order
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Switch tab to the first order’s status
      const firstStatus =
        allOrders.length > 0 &&
        allOrders[0].orderState &&
        allOrders[0].orderState.workerStatus;

      let tabIndex = 0;
      switch (firstStatus) {
        case WORKER_ORDER_STATUS.NOT_STARTED:
        case WORKER_ORDER_STATUS.PP_ASSIGNED:
          tabIndex = 0;
          break;
        case WORKER_ORDER_STATUS.PP_IN_PROGRESS:
          tabIndex = 1;
          break;
        default:
          tabIndex = 2;
      }

      setValue(tabIndex);
    }
  }, [data]);

  // Split search results per tab
  const assignedOrders = searchResult.filter(
    (o) =>
      o.orderState &&
      [WORKER_ORDER_STATUS.NOT_STARTED, WORKER_ORDER_STATUS.PP_ASSIGNED].includes(
        o.orderState.workerStatus
      )
  );

  const inProgressOrders = searchResult.filter(
    (o) => o.orderState && o.orderState.workerStatus === WORKER_ORDER_STATUS.PP_IN_PROGRESS
  );

  const completedOrders = searchResult.filter(
    (o) =>
      o.orderState &&
      ![WORKER_ORDER_STATUS.NOT_STARTED, WORKER_ORDER_STATUS.PP_ASSIGNED, WORKER_ORDER_STATUS.PP_IN_PROGRESS].includes(
        o.orderState.workerStatus
      )
  );


  return (
    <Box sx={{ width: '100%' }}>
      {/* Tabs + Search */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="packing worker tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Assigned Orders" {...a11yProps(0)} />
          <Tab label="In Progress Orders" {...a11yProps(1)} />
          <Tab label="Completed Orders" {...a11yProps(2)} />
        </Tabs>

        {/* Search Field */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <TextField
            label="Search Order ID"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
             sx={{
              '& .MuiInputBase-input': {
                color: 'black', // <-- text color
              },
              '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ccc', // optional: outline color
              },
              '& .MuiInputLabel-root': {
                color: 'black', // label color
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    aria-label="Search orders"
                    onClick={handleManualSearch}
                  >
                    <SearchIcon aria-hidden="true" color="action" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear order search"
                    onClick={() => {
                      setSearchText('');
                      setSearchResult([]);
                      previousSearch.current = '';
                    }}
                  >
                    <ClearIcon aria-hidden="true" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Tab Panels */}
      <CustomTabPanel value={value} index={0}>
        <PackageOrderList searchResult={assignedOrders} scrollRef={scrollRef} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <PackageNewOrderList searchResult={inProgressOrders} scrollRef={scrollRef} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <PackagePackedOrderList searchResult={completedOrders} scrollRef={scrollRef} />
      </CustomTabPanel>
    </Box>
  );
}

export default withRouter(PackingWorkerTab);